/**
 * VectorSearchResult.tsx - Search Result Citation Component
 * Updated: Show less by default (only small text + similarity index), no borders
 */

import { usePostMetadata } from "@/hooks/usePostMetadata";
import { Card, HStack, Link, VStack, Box } from "@chakra-ui/react";
import { usePost, bookCairReaderUrl } from "core";
import { t } from "i18next";
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { aiChatOriginQuery } from "@/utilities/aiChatOrigin";
import { VectorSearchChunk } from "@/types/post.types";
import { SmartText } from "@/ui/components/ui/SmartText";
import { formatDateToDDMMYYYY } from "@/utilities/formatDate";
import { useFilterContext } from "@/contexts/FilterContext";
import { getTranslationKey } from "@/utilities/getTranslationKey";
import { HIDE_UNRESOLVED_CATEGORIES, isLabelResolved } from "@/utilities/deweyCodeParser";

interface VectorSearchResultProps {
    chunk: VectorSearchChunk;
}

export const VectorSearchResult: React.FC<VectorSearchResultProps> = ({ chunk }) => {
    const abbreviatedScore = chunk.score.toFixed(3);
    const [isExpanded, setIsExpanded] = useState(false);

    const { data: postObj } = usePost(chunk.metadata.postId);
    const { findCategoryLabel } = useFilterContext();
    const post = postObj;

    // The Chat-AI circle in the URL is the "origin" we forward from.
    const { circleId } = useParams();

    const [postMetadataId, setPostMetadataId] = useState<string>('');

    useEffect(() => {
        if (!post) return;
        setPostMetadataId(post._id);
    }, [post]);

    const TextArray = chunk.text.trim().split(" ");
    const startWords = TextArray.slice(0, 1).join("%20");
    const endWords = TextArray.slice(-1).join("%20");

    const { data: postObjMetadata } = usePostMetadata(postMetadataId);
    const postMetadata = postObjMetadata;
    const metadata = postMetadata?.post?.metadata;

    const normalizeToList = useMemo(() => (value: unknown): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value.map((v) => String(v));
        return [String(value)];
    }, []);

    const categories = normalizeToList(chunk?.metadata?.categories);
    const locations = normalizeToList(chunk?.metadata?.locations);
    const organizations = normalizeToList(chunk?.metadata?.organizations);
    const persons = normalizeToList(chunk?.metadata?.persons);
    const tags = normalizeToList(chunk?.metadata?.tags);
    console.log('chunk', chunk);
    const mappedCategories = useMemo(() => {
        const results: string[] = [];
        for (const code of categories) {
            const rawLabel = findCategoryLabel(code);
            
            // Check if we resolved a label or just got the code back
            if (!isLabelResolved(code, rawLabel)) {
                // No label found - skip if feature flag is enabled
                if (HIDE_UNRESOLVED_CATEGORIES) {
                    continue;
                }
                results.push(code);
                continue;
            }
            
            const translationKey = getTranslationKey(rawLabel);
            const translated = t(translationKey, { ns: 'topics' });
            results.push(translated !== translationKey ? translated : rawLabel);
        }
        return results;
    }, [categories, findCategoryLabel]);

    const ownTitle = postMetadata?.post?.metadata?.title || post?.title || '';
    // A book's pages all carry the BOOK's title in their chunk metadata (node-api
    // getChunkMetadata stamps the thread root's onto every page), so without the page
    // number an answer drawing on several pages of one book shows the same "From:" line
    // over and over. The number can only come from the chunk (attachSourcePageNumbers
    // stamps it): `post` here is never the page — GET /posts/:id resolves a page to its
    // thread root (PostsHelper.loadPost, root=true) — and serializePostForResponse drops
    // documentPageNumber from every response anyway.
    const bookTitle = chunk.metadata?.title || ownTitle;
    const pageNumber = chunk.metadata?.pageNumber;
    const titleText = !post
        ? ''
        : (bookTitle && pageNumber != null)
            ? `${bookTitle} - ${t("page")} ${pageNumber}`
            : ownTitle;

    const chunkHash = `#?chunk=${chunk?.metadata?.postId}${chunk?.metadata?.chunkId ? `-${chunk?.metadata?.chunkId}` : ""}`;
    // On localhost the chat runs on a different port than the main react-app (3000).
    // For production both live on the same origin, so we keep window.location.host.
    const appOrigin = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
        ? "http://localhost:3000"
        : `https://${window.location.host}`;
    const href = post && post.group?.group?._id && post._id
        ? (postMetadata?.post?.metadata?.rights?.includes(".")
            ? `https://${postMetadata.post.metadata.rights.replace("http://", "").replace("https://", "")}#:~:text=${startWords},${endWords}`
            : `${appOrigin}${bookCairReaderUrl(post._id)}${aiChatOriginQuery(circleId)}${chunkHash}`)
        : '#';

    const fullText = chunk.text.trim();
    const MAX_CHARS = 150; // Reduced for "show less" default
    const isTruncated = fullText.length > MAX_CHARS;
    const displayText = isExpanded || !isTruncated ? fullText : fullText.slice(0, MAX_CHARS);

    if (!post) return null;

    return (
        <Link
            target="_blank"
            width="100%"
            href={href}
            _hover={{ textDecoration: "none" }}
        >
            <Card.Root
                variant="outline"
                width="100%"
                border="0"
                borderRadius="8px"
                background="rgba(255, 255, 255, 0.95)"
                transition="all 0.2s"
                _hover={{ backgroundColor: "rgba(255, 255, 255, 0.80)", boxShadow: "var(--cair-shadow-sm)" }}
            >
                <Card.Body paddingY="2" paddingX="3">
                    <VStack align="stretch" gap={1}>
                        {/* Default: Small text snippet */}
                        {titleText && (
                            <SmartText dir="auto" fontSize="xs" color="gray.600" fontWeight="medium" marginBottom={1}>
                                {t("from")}: <em dir="auto">{titleText}</em>
                            </SmartText>
                        )}
                        {metadata?.author && (
                            <SmartText dir="auto" fontSize="xs" color="gray.600">
                                {t("by")}: {metadata.author}
                            </SmartText>
                        )}

                        <SmartText
                            text={displayText + (isTruncated && !isExpanded ? "…" : "")}
                            dir="auto"
                            fontSize="xs"
                            lineHeight="1.5"
                            color="gray.700"
                            marginTop={1}
                        >
                            {displayText}{isTruncated && !isExpanded && "…"}
                        </SmartText>

                        {/* Default: Similarity index - always visible */}
                        <HStack justifyContent="space-between" alignItems="center">
                            {isTruncated && (
                                <Link
                                    as="button"
                                    type="button"
                                    fontSize="xs"
                                    color="#333333"
                                    textDecoration="underline"
                                    cursor="pointer"
                                    _hover={{ color: "var(--cair-accent-hover)" }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsExpanded((prev) => !prev);
                                    }}
                                >
                                    {isExpanded ? t("see_less") : t("see_more")}
                                </Link>
                            )}
                            <SmartText dir="auto" fontSize="xs" color="gray.500" fontWeight="medium" marginLeft="auto">
                                {abbreviatedScore}
                            </SmartText>
                        </HStack>

                        {/* Expanded: Show all metadata */}
                        {isExpanded && (
                            <Box paddingTop={2} borderTop="1px solid" borderColor="gray.100">
                                {mappedCategories.length > 0 && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        Categories: {mappedCategories.join(", ")}
                                    </SmartText>
                                )}
                                {locations.length > 0 && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        Locations: {locations.join(", ")}
                                    </SmartText>
                                )}
                                {organizations.length > 0 && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        Organizations: {organizations.join(", ")}
                                    </SmartText>
                                )}
                                {persons.length > 0 && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        Persons: {persons.join(", ")}
                                    </SmartText>
                                )}
                                {metadata?.publicationDate && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        Date: {formatDateToDDMMYYYY(metadata.publicationDate)}
                                    </SmartText>
                                )}
                                {tags.length > 0 && (
                                    <SmartText dir="auto" fontSize="xs" color="gray.600">
                                        {t("tags")}: {tags.join(", ")}
                                    </SmartText>
                                )}
                            </Box>
                        )}
                    </VStack>
                </Card.Body>
            </Card.Root>
        </Link>
    );
};